// Public policy content. Payment processors and customers read these pages
// before an account is approved, so every statement here must match the
// behaviour in README.md and docs/payment-label-exceptions.md.

export const business = {
  legalName: 'Creoforis, LLC',
  brand: 'RX Routing',
  street: '7125 Bay Dr',
  cityStateZip: 'Miami Beach, FL 33141',
  country: 'United States',
  supportEmail: 'support@rxrouting.com',
}

export const policyUpdated = 'October 6, 2026'

export type PolicyBlock = string | { list: string[] }
export type PolicySection = { heading: string; blocks: PolicyBlock[] }
export type PolicySlug = 'terms' | 'refunds' | 'shipping'
export type Policy = { slug: PolicySlug; title: string; navLabel: string; summary: string; sections: PolicySection[] }

const terms: Policy = {
  slug: 'terms',
  title: 'Terms and Conditions',
  navLabel: 'Terms',
  summary:
    'The agreement between you and Creoforis, LLC for use of the RX Routing package forwarding service.',
  sections: [
    {
      heading: 'Who we are',
      blocks: [
        `RX Routing is a package forwarding service operated by ${business.legalName}, ${business.street}, ${business.cityStateZip}, ${business.country}. In these terms, "we", "us" and "our" mean ${business.legalName}, and "you" means the account holder using the service at rxrouting.com.`,
        'By creating an account or placing a forwarding order, you agree to these terms, our Refund Policy, and our Shipping Policy.',
      ],
    },
    {
      heading: 'What the service does',
      blocks: [
        'We receive a package addressed to you at our U.S. intake warehouse, verify it against the order you paid for, and forward it to the destination address you provided.',
        'We are not a retailer. We do not sell, own, supply, inspect for quality, or warrant any product we forward. You buy products separately and directly from the U.S. seller of your choice.',
        'Your payment to us covers forwarding only: carrier postage, our routing fee, and an estimated payment processing fee. It never includes the price of the goods.',
      ],
    },
    {
      heading: 'Your account',
      blocks: [
        'You must be at least 18 years old, or the age of majority where you live, to hold an account.',
        'You agree to provide accurate information, to verify your email address, and to keep your sign-in credentials confidential. You are responsible for activity under your account.',
        'We may suspend or close an account that violates these terms, that we reasonably believe is being used unlawfully or fraudulently, or where we are required to do so by law or by a payment or carrier partner.',
      ],
    },
    {
      heading: 'Items we forward',
      blocks: [
        'We currently forward two product formats only:',
        { list: ['Oral tablets and capsules', 'Topical serum'] },
        'We do not accept anything else. A package whose contents fall outside these formats is held for your instruction and, failing that, disposed of as the law allows. We do not return packages to senders.',
        'You confirm that each package you send to us may lawfully be exported from the United States and imported into the destination country, and that you have the right to arrange its shipment.',
      ],
    },
    {
      heading: 'Quotes, pricing, and orders',
      blocks: [
        'When you place an order you choose a destination and a product format. That format supplies a versioned shipping preset: packed weight, dimensions, customs description, declared value, HS code, and origin country.',
        'We request live rates for your destination from USPS, UPS, and FedEx, and show you only exact, purchasable rates from those carriers. You select the rate you want.',
        'The total shown before payment is the total you pay. We snapshot the postage, routing fee, estimated processing fee, destination, parcel facts, and customs data at that moment.',
        'Rates can change until your order is authorized, and an unused quote may expire. Once your order is authorized, the price is fixed: if your actual packed parcel costs more to ship than the preset quoted, we absorb the difference and do not bill you again.',
      ],
    },
    {
      heading: 'Payment',
      blocks: [
        'Card details are tokenized in your browser by our payment gateway. We never receive or store your full card number.',
        'We charge the exact snapshotted total for your order and nothing else. We do not store your card for future charges or enroll you in a subscription.',
      ],
    },
    {
      heading: 'Your RXID and the forwarding address',
      blocks: [
        'After your payment is authorized, we issue one order ID and one RXID for that order. An RXID belongs to a single order and a single package.',
        `For new forwarding orders, your complete U.S. forwarding address appears on your order page after payment is confirmed. Use your own name and keep the unit and RXID together on address line 2. Existing orders retain their original warehouse: if your order page asks you to email ${business.supportEmail}, follow those instructions and use the address support provides for that order.`,
        'You must use the routing line exactly as we provide it. A package that arrives without a valid, active RXID cannot be reliably matched to you, which may delay or prevent forwarding.',
        'Do not reuse a forwarding address or RXID for a different order, and do not share either with anyone else.',
      ],
    },
    {
      heading: 'Customs and compliance',
      blocks: [
        'For an international destination, you review the customs facts, confirm your full legal name, and certify the declaration before you pay. We record who signed, which version they certified, and when.',
        'You are responsible for the accuracy of that declaration. If the packed parcel differs enough to change the certified customs facts, we will ask you to certify the corrected declaration before we ship.',
        'Any duties, taxes, brokerage, or fees charged by the destination country are the responsibility of the recipient and are not included in what you pay us.',
        'We may refuse, hold, or dispose of any package we reasonably believe is unlawful, unsafe, misdeclared, or outside our supported formats.',
      ],
    },
    {
      heading: 'Delivery, cancellation, and refunds',
      blocks: [
        'Dispatch timing, tracking, delivery estimates, and claims for lost or damaged shipments are covered in our Shipping Policy.',
        'Cancellations and refunds are covered in our Refund Policy. In summary: you may cancel for a refund of the forwarding total minus a flat $5 cancellation fee as long as nothing has been shipped to us under your RXID, and we need the retailer\u2019s cancellation confirmation if you had already placed an order. Once a retailer has shipped to the forwarding address we gave you, the forwarding charge is final.',
      ],
    },
    {
      heading: 'Unmatched and unclaimed packages',
      blocks: [
        'If a package arrives without a matching active RXID, or if the order it belongs to has been cancelled or refunded, we hold the package and attempt to contact you at your account email.',
        'If we receive no workable instruction within 30 days, we may dispose of the package as the law allows. We do not return packages, and storage beyond that period is not guaranteed.',
      ],
    },
    {
      heading: 'Limitation of liability',
      blocks: [
        'The service is provided on an "as is" and "as available" basis. We do not guarantee uninterrupted availability.',
        'We are not liable for the acts or omissions of carriers, customs authorities, payment providers, or the retailers you buy from, nor for the condition, legality, authenticity, or fitness of the goods inside a package.',
        'To the fullest extent permitted by law, our total liability for any order is limited to the amount you paid us for that order, plus any carrier coverage we actually recover on your behalf.',
        'We are not liable for indirect, incidental, special, or consequential losses, including lost profits or lost opportunity.',
        'Nothing in these terms limits liability that cannot be limited or excluded under applicable law.',
      ],
    },
    {
      heading: 'Acceptable use',
      blocks: [
        'You agree not to use the service to ship prohibited or restricted goods, to make a false or misleading customs declaration, to evade sanctions, duties, or import restrictions, to resell or share access to your account, or to interfere with the security or operation of our systems.',
      ],
    },
    {
      heading: 'Changes to these terms',
      blocks: [
        'We may update these terms. When we make a material change we post the revised version here with a new effective date. Orders are governed by the version in effect when the order was authorized, and continuing to use the service after a change means you accept the revised terms.',
      ],
    },
    {
      heading: 'Governing law',
      blocks: [
        'These terms are governed by the laws of the State of Florida, without regard to its conflict of law rules. The state and federal courts located in Miami-Dade County, Florida have exclusive jurisdiction over any dispute, and you and we consent to venue there.',
      ],
    },
    {
      heading: 'Contact',
      blocks: [
        `${business.legalName}, ${business.street}, ${business.cityStateZip}, ${business.country}. Email ${business.supportEmail}.`,
      ],
    },
  ],
}

const refunds: Policy = {
  slug: 'refunds',
  title: 'Refund Policy',
  navLabel: 'Refunds',
  summary:
    'When a forwarding order can be cancelled, the proof we need, and why the charge is final once a package has been sent to us.',
  sections: [
    {
      heading: 'What this policy covers',
      blocks: [
        'This policy covers the forwarding charges you pay to RX Routing: carrier postage, our routing fee, and the estimated processing fee.',
        'It does not cover the price of the goods themselves. We are not the seller. For a product refund, an exchange, or a warranty claim, contact the retailer you bought from.',
      ],
    },
    {
      heading: 'Why the timing matters',
      blocks: [
        'You pay for forwarding before you buy your product. Once your order is authorized we issue your RXID and release a U.S. forwarding address, which you enter as the shipping address when you check out with the retailer.',
        'Placing a retailer order does not make your forwarding charge final. What matters is whether the retailer has shipped the package to us. If you cancel the retailer order before it ships, you can request a forwarding refund with proof of cancellation and that the order never shipped.',
      ],
    },
    {
      heading: 'Cancelling before the retailer ships',
      blocks: [
        'You may cancel for a refund of the forwarding total minus a flat $5 cancellation fee, as long as nothing has been shipped to us under your RXID.',
        'That covers two situations:',
        { list: [
          'You have not yet placed an order with any retailer using the forwarding address.',
          'You placed an order and cancelled it with the retailer before it shipped.',
        ] },
        'If you placed an order at all, we need proof that it was cancelled and never dispatched: the retailer\u2019s own cancellation confirmation, showing the order number, the cancellation, and that no shipment left their warehouse. An order still marked processing, a screenshot of an empty cart, or your own account of the cancellation is not enough, because we cannot verify a retailer\u2019s records on your behalf.',
        'Once we confirm it, we refund the forwarding total less the $5 fee and revoke your RXID. That address and RXID must not be used again.',
      ],
    },
    {
      heading: 'Once a package has been sent to us',
      blocks: [
        'If a retailer has shipped to the forwarding address we gave you, the forwarding charge is final and cannot be refunded. That applies whether the package is in transit, already received, or sitting in our warehouse, and whether or not you still want the product.',
        'Your payment buys the forwarding address, the RXID, and the reserved intake slot. Once a parcel is moving toward us we take custody of goods we did not buy, cannot return to the retailer on your behalf, and cannot leave unattended. We are not able to take on that coordination for an order the buyer no longer wants.',
        'If you change your mind after a package has reached us, there are two outcomes. We forward it to your destination as paid, which is the default, or you authorize us to dispose of it. We do not return packages to retailers or to U.S. addresses, and we cannot hold a parcel while you arrange a return with the seller. The forwarding charge stands either way.',
      ],
    },
    {
      heading: 'Once a shipping label is bought',
      blocks: [
        'Postage is not refundable once we have purchased a label and a carrier has accepted the parcel. Carriers do not refund used labels.',
        'Payment refunds and carrier label refunds are tracked separately, because they settle on different systems and timelines. We never treat one as proof of the other.',
      ],
    },
    {
      heading: 'What we cannot refund',
      blocks: [
        { list: [
          'Any forwarding charge once a package has been shipped to us under your RXID.',
          'The $5 cancellation fee on an approved cancellation.',
          'Duties, taxes, or brokerage fees charged by the destination country. These are paid to the authorities, not to us.',
          'Costs caused by an inaccurate destination address or customs declaration you provided or certified.',
          'Packages seized, destroyed, returned, or refused by customs because the contents were ineligible or the declaration was inaccurate.',
          'Storage or disposal costs on a cancelled, abandoned, or unclaimed package.',
          'The price of the goods, which you paid to a third-party retailer.',
        ] },
      ],
    },
    {
      heading: 'Lost or damaged shipments',
      blocks: [
        'If a shipment we sent is lost or arrives damaged, contact us with your order ID and RXID and keep all packaging. We file a claim with the carrier on your behalf.',
        'Carrier claims are decided by the carrier on its own timeline. Where a claim is paid, we pass the recovery on to you. Where we are at fault, we also refund our routing fee.',
      ],
    },
    {
      heading: 'How refunds are issued',
      blocks: [
        'Approved refunds are returned to the original payment method only. We cannot refund to a different card, account, or person.',
        'Once approved, a refund usually appears within 5 to 10 business days, though the exact timing depends on your bank or card issuer. Approval by our gateway is not the same as final settlement by your bank.',
        'A fully refunded order has its authorization revoked. Its RXID stops being valid and cannot be used to send another package.',
      ],
    },
    {
      heading: 'Disputes and chargebacks',
      blocks: [
        'Please contact us before opening a dispute with your bank. Most issues are resolved faster directly.',
        'While an order is under payment dispute or review, we hold the order and suspend its RXID and any unshipped fulfillment until the dispute is resolved.',
      ],
    },
    {
      heading: 'How to request a cancellation or refund',
      blocks: [
        `Email ${business.supportEmail} from the email address on your account. Include your order ID, your RXID, and the reason for the request.`,
        'If you placed a retailer order using the forwarding address, attach the retailer\u2019s cancellation confirmation in the same message. We cannot begin a cancellation without it.',
      ],
    },
    {
      heading: 'Contact',
      blocks: [
        `${business.legalName}, ${business.street}, ${business.cityStateZip}, ${business.country}. Email ${business.supportEmail}.`,
      ],
    },
  ],
}

const shipping: Policy = {
  slug: 'shipping',
  title: 'Shipping Policy',
  navLabel: 'Shipping',
  summary:
    'Where we ship, how long forwarding takes, what the quoted price covers, and who is responsible for duties.',
  sections: [
    {
      heading: 'Where and how we ship',
      blocks: [
        'We forward packages from our U.S. intake warehouse to destinations in the 50 United States and the District of Columbia, and to international addresses we can lawfully and reliably serve.',
        'We purchase labels only from USPS, UPS, and FedEx. The rates you see are live, purchasable rates from those carriers for your specific destination and parcel.',
      ],
    },
    {
      heading: 'What we forward',
      blocks: [
        'We forward oral tablets and capsules, and topical serum. We do not forward any other product format. Keeping the scope narrow is what lets our warehouse handle intake and customs consistently.',
      ],
    },
    {
      heading: 'Getting your forwarding address',
      blocks: [
        'The forwarding address is released only after your order is paid and authorized. It is never shown in the application before that.',
        `For new orders, copy the complete address from your order page and use every line at the U.S. retailer’s checkout. Existing orders keep their original warehouse and routing instructions. If your order page asks you to contact support, email ${business.supportEmail} from your account email with your order ID and RXID. Do not use a newer order’s address for an existing order.`,
        'The routing line is specific to one order and one package. Use it exactly as given, and do not reuse it for a later order.',
        'You may cancel for a refund of the forwarding total minus a flat $5 cancellation fee as long as the retailer has not shipped the package to us. If you have already placed a retailer order, provide the retailer\u2019s cancellation confirmation showing that the order was cancelled and never shipped. Once the retailer ships to your forwarding address, the forwarding charge becomes final. See our Refund Policy for details.',
      ],
    },
    {
      heading: 'Processing time',
      blocks: [
        'Our warehouse matches an arriving package by its RXID, verifies it against the order you paid for, and records the final packed weight and dimensions.',
        'Verified packages are normally dispatched within one business day of receipt.',
        'Dispatch can take longer when:',
        { list: [
          'the package arrives without a valid or active RXID',
          'the contents do not match the product format on the order',
          'corrected packed facts require you to re-certify the customs declaration',
          'the carrier service you selected is temporarily unavailable for the route',
        ] },
        'If any of these apply, we hold the parcel and contact you rather than shipping something we cannot stand behind.',
      ],
    },
    {
      heading: 'What your quoted price covers',
      blocks: [
        'Your quote is built from a versioned preset for the product format you chose, priced against live carrier rates for your destination.',
        'The total is fixed when your order is authorized. If the actual packed parcel turns out to cost more than quoted, we absorb the difference and do not bill you again.',
        'If the corrected facts change a certified customs declaration, we need your re-certification before the parcel can ship. That protects you from a declaration you did not agree to.',
      ],
    },
    {
      heading: 'Tracking',
      blocks: [
        'Every shipment we send includes carrier tracking. Your tracking number appears on the order in your dashboard as soon as the label is purchased, and the order status updates as the carrier reports movement.',
      ],
    },
    {
      heading: 'Delivery estimates',
      blocks: [
        'Transit times shown with a rate are the carrier’s estimates, not guarantees. Customs inspection, weather, carrier backlogs, and local delivery conditions can all add time. We cannot guarantee a delivery date and do not refund postage for a late delivery the carrier still completes.',
      ],
    },
    {
      heading: 'Customs, duties, and taxes',
      blocks: [
        'For international shipments you review and certify the customs declaration before paying. You are responsible for its accuracy.',
        'Duties, taxes, and brokerage fees charged by the destination country are billed by that country to the recipient. They are not included in what you pay us, and we cannot estimate or prepay them.',
        'An inaccurate declaration can lead to seizure, return, delay, or penalties, and those costs are not refundable.',
      ],
    },
    {
      heading: 'Address accuracy',
      blocks: [
        'We check your destination through address capture and a carrier format check, then ask you to confirm the final version before saving it. The address you confirm is the address we ship to.',
        'If a shipment is undeliverable because of an incorrect or incomplete address, the carrier either disposes of it or sends it back to us. A parcel that comes back to us follows the same rule as any other package in our warehouse: we can forward it again once you have paid for a new forwarding order, or dispose of it on your authorization. The original forwarding charge is not refundable.',
      ],
    },
    {
      heading: 'Restricted destinations',
      blocks: [
        'We may decline a destination we cannot lawfully or reliably serve.',
        'Military mail (APO, FPO, and DPO) and U.S. territories are handled through our international customs flow, because requirements vary by route. We do not treat them as ordinary domestic addresses.',
      ],
    },
    {
      heading: 'Lost, delayed, or damaged shipments',
      blocks: [
        `Contact ${business.supportEmail} with your order ID and RXID. For damage, keep the parcel and all packaging, since carriers require them to assess a claim.`,
        'We file the carrier claim and share the outcome with you. Claim decisions and timelines belong to the carrier. See our Refund Policy for what we refund in these cases.',
      ],
    },
    {
      heading: 'Contact',
      blocks: [
        `${business.legalName}, ${business.street}, ${business.cityStateZip}, ${business.country}. Email ${business.supportEmail}.`,
      ],
    },
  ],
}

export const policies: Policy[] = [terms, refunds, shipping]
export const policyBySlug = (slug: PolicySlug) => policies.find(policy => policy.slug === slug)
